import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Logo from "../../../../authentication/components/Logo";
import { ExportMenu } from "../components/ExportMenu";
import { exportToPDF } from "../utils/exportToPdf";
import { exportToExcel } from "./utils/exportToExcel";
import { formatDate, formatTime, generateFileName } from "../utils/helpers";
import { useAuth } from "../../../../context/AuthContext";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { usePortal } from "../../../../context/PortalsContext";
import { useToast } from "../../../../context/ToastContext";
import { AssesmentReportTable } from "./components/AssesmentReportTable";
import { AssesmentPdfReportTable } from "./components/AssesmentPdfReportTable";
import {
  AssessmentReportAttempt,
  AssessmentReportPdfData,
  ReportTemplateTypeEnum,
} from "../../../../services/api/types/reports/reports";
import { PackageType } from "../../../../services/api/types/assignments/assignment";
import { getAssesmentReportData } from "../../../../services/api/apiClient";
import { UserCertificateResult } from "../../../../services/api/types/certificates/certificates";
import { CertificateView } from "../../../Certificates/components/CertificateView";
import useScreenSize from "../../../../hooks/useScreenSize";
import { usePageCustomisation } from "../../../../hooks/usePageCustomisation";

export const AssesmentReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { portalUid: urlPortalUid } = useParams();
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { customisations } = useCustomisation();
  const { showToast } = useToast();
  usePageCustomisation("CERTIFICATES");
  const {
    GPC: { navbarSettings },
  } = customisations.customisations;

  const gpcNavbarBgColor = navbarSettings?.navbarBgColor || undefined;

  const currentDate = new Date();
  const formattedDate = formatDate(currentDate);
  const formattedTime = formatTime(currentDate);

  const userName = auth.user
    ? `${auth.user.firstName || ""} ${auth.user.lastName || ""}`.trim() ||
    "Unknown User"
    : "Unknown User";

  const reportTitle = "Assessment Report";
  const userAssignmentUid = searchParams.get("userAssignmentUid") ?? undefined;
  const rawPackageType = searchParams.get("packageType");
  const packageType =
    rawPackageType === PackageType.PdfUpload ||
      rawPackageType === PackageType.VideoUpload
      ? rawPackageType
      : undefined;

  const [attempts, setAttempts] = useState<AssessmentReportAttempt[]>([]);
  const [acknowledgementData, setAcknowledgementData] =
    useState<AssessmentReportPdfData | null>(null);
  const [certificateUid, setCertificateUid] = useState<string | null>(null);
  const [assignmentName, setAssignmentName] = useState<string>("");
  const [assigneeName, setAssigneeName] = useState<string>("");
  const [assigneeInitials, setAssigneeInitials] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [certificateForPDF, setCertificateForPDF] =
    useState<UserCertificateResult | null>(null);
  const [certificatesLoading, setCertificatesLoading] = useState(false);

  const { isTablet } = useScreenSize();

  const fetchData = async (portalId: string, uid: string) => {
    setLoading(true);
    try {
      const data = await getAssesmentReportData(portalId, {
        userAssignmentUid: uid,
        ...(packageType && { packageType }),
      });
      setAttempts(data.attempts ?? []);
      setAcknowledgementData(
        packageType === PackageType.PdfUpload ||
          packageType === PackageType.VideoUpload
          ? {
            ackResponse: data.ackResponse ?? null,
            comment: data.comment ?? null,
            submittedAt: data.submittedAt ?? null,
          }
          : null,
      );
      setCertificateUid(data.certificateUid);
      setAssignmentName(data.assignment);
      setAssigneeName(
        `${data.firstName || ""} ${data.lastName || ""}`.trim() ||
        "Unknown User",
      );
      setAssigneeInitials(
        `${data.firstName?.[0] || ""}${data.lastName?.[0] || ""}`.toUpperCase(),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const portalId = urlPortalUid || portals.selectedPortal?.portalUid;

    if (!portalId || !userAssignmentUid) return;

    fetchData(portalId, userAssignmentUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPortalUid, portals.selectedPortal, userAssignmentUid]);

  const handleExportPDF = async () => {
    await exportToPDF(
      "assesment-report",
      generateFileName(ReportTemplateTypeEnum.ASSESSMENT_REPORT),
    );
  };

  const handleExportExcel = () => {
    exportToExcel({
      attempts,
      acknowledgementData,
      packageType,
      reportName: reportTitle,
      generatedBy: userName,
      date: formattedDate,
      time: formattedTime,
      fileName: generateFileName(ReportTemplateTypeEnum.ASSESSMENT_REPORT),
    });
  };

  const generateCertificatePDF = async (
    certificate: UserCertificateResult,
  ): Promise<{ blob: Blob; filename: string }> => {
    const sanitizedTitle = certificate.assignment
      .replace(/[\\/:*?"<>|]+/g, "")
      .trim();
    const filename = `${sanitizedTitle}_certificate.pdf`;

    setCertificateForPDF(certificate);
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const element = document.getElementById(certificate.certificateUid);
      if (!element) {
        throw new Error(
          `Certificate element not found with ID: ${certificate.certificateUid}`,
        );
      }

      const images = element.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map((img) => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
            setTimeout(() => resolve(), 5000);
          });
        }),
      );
      await new Promise((resolve) => setTimeout(resolve, 200));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = (canvas.height * pageWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);

      setCertificateForPDF(null);
      return { blob: pdf.output("blob"), filename };
    } catch (error) {
      setCertificateForPDF(null);
      throw error;
    }
  };

  const downloadCertificatePDF = async (certificate: UserCertificateResult) => {
    const { blob, filename } = await generateCertificatePDF(certificate);
    saveAs(blob, filename);
  };

  const handleCertificateDownload = async () => {
    if (!certificateUid) return;

    const isUploadAssignment =
      packageType === PackageType.PdfUpload ||
      packageType === PackageType.VideoUpload;

    let certificate: UserCertificateResult;

    if (isUploadAssignment) {
      certificate = {
        certificateUid,
        assignment: assignmentName,
        startDate: acknowledgementData?.submittedAt ?? null,
        completionDate: acknowledgementData?.submittedAt ?? null,
        timeSpent: 0,
        bestScore: 0,
        packageType,
      };
    } else {
      const passedAttempt = attempts.find((a) => a.status === "passed");
      if (!passedAttempt) {
        showToast(
          "No passed attempt found for certificate download.",
          "warning",
        );
        return;
      }

      certificate = {
        certificateUid,
        assignment: assignmentName,
        startDate: passedAttempt.started,
        completionDate: passedAttempt.completed,
        timeSpent: passedAttempt.timeSpent ?? 0,
        bestScore: passedAttempt.score ?? 0,
      };
    }

    setCertificatesLoading(true);
    try {
      await downloadCertificatePDF(certificate);
      showToast("Certificate downloaded successfully.", "success");
    } catch (error) {
      console.error("Error downloading certificates:", error);
      showToast("Failed to download certificates. Please try again.", "error");
    } finally {
      setCertificatesLoading(false);
    }
  };

  return (
    <>
      {certificateForPDF && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            overflow: "hidden",
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#2B2B2B",
              minHeight: "100vh",
              py: 6,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CertificateView
              certificate={certificateForPDF}
              recipientName={assigneeName}
            />
          </Box>
        </Box>
      )}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          py: { xs: 0, md: 4 },
          justifyContent: "center",
        }}
      >
        <Box
          id="assesment-report"
          sx={{
            backgroundColor: "#fff",
            width: "100%",
            maxWidth: "1315px",
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{ backgroundColor: gpcNavbarBgColor, p: 1 }}
          >
            <Logo isLoginPageLogo={false} />
            <ExportMenu
              className="export-buttons"
              onExportPDF={handleExportPDF}
              onExportExcel={handleExportExcel}
              onExportCertificates={handleCertificateDownload}
              isCertificatesLoading={certificatesLoading}
              isCertificateDownloadEnabled={!!certificateUid}
            />
          </Stack>

          <Divider sx={{ borderColor: "#5354D8", mb: 2, mt: 3 }} />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{ flexWrap: "wrap", rowGap: 1.5 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: isTablet ? "40" : "48",
                  height: isTablet ? "40" : "48",
                  bgcolor: "#F2F4F7",
                  color: "#667085",
                  fontSize: isTablet ? 14 : 16,
                  fontWeight: 600,
                }}
              >
                {assigneeInitials}
              </Avatar>
              <Stack spacing={0.5} alignItems="start">
                <Typography sx={{ fontSize: 12, color: "#667085" }}>
                  {reportTitle}
                </Typography>
                <Typography
                  sx={{
                    color: "#1F2937",
                    fontWeight: 600,
                    fontSize: isTablet ? 20 : 24,
                  }}
                >
                  {assigneeName}
                </Typography>
                {assignmentName && (
                  <Typography sx={{ fontSize: 14, color: "#667085" }}>
                    {assignmentName}
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Stack spacing={0.5} alignItems="start">
              <Box display="flex" alignItems="center" gap={0.75}>
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 12, color: "#667085" }}
                />
                <Typography sx={{ fontSize: 14, color: "#667085" }}>
                  {formattedDate}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.75}>
                <AccessTimeOutlinedIcon
                  sx={{ fontSize: 12, color: "#667085" }}
                />
                <Typography sx={{ fontSize: 14, color: "#667085" }}>
                  {formattedTime}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.75}>
                <PersonOutlinedIcon sx={{ fontSize: 12, color: "#667085" }} />
                <Typography sx={{ fontSize: 14, color: "#667085" }}>
                  Generated by {userName}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ borderColor: "#5354D8", mb: 2 }} />

          <Typography fontSize={20} fontWeight={600} color="#1F2937" mb={2}>
            Additional Data Points / Question-level Data
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : packageType === PackageType.PdfUpload ||
            packageType === PackageType.VideoUpload ? (
            <AssesmentPdfReportTable data={acknowledgementData} />
          ) : (
            <AssesmentReportTable attempts={attempts} />
          )}
        </Box>
      </Box>
    </>
  );
};
